import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Leaf, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const email = params.get("email") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. You can sign in now.");
      navigate("/admin/login");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link
          to="/admin/login"
          className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#1a2947] mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <div className="bg-white border border-[#e6e2d6] rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-10 w-10 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
            <span className="font-serif text-2xl text-stone-900">Bailey Family Reunion</span>
          </div>
          <div className="label-eyebrow mb-2">Reset password</div>
          <h1 className="font-serif text-3xl text-stone-900 mb-2">
            Pick a new password.
          </h1>
          {email && (
            <p className="text-stone-500 text-sm mb-6">for {email}</p>
          )}
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="rp-pw" className="label-eyebrow">New password</Label>
              <Input
                id="rp-pw"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                minLength={8}
                className="mt-2 bg-stone-50 border-stone-200"
                data-testid="reset-pw-input"
              />
            </div>
            <div>
              <Label htmlFor="rp-pw2" className="label-eyebrow">Confirm password</Label>
              <Input
                id="rp-pw2"
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                minLength={8}
                className="mt-2 bg-stone-50 border-stone-200"
                data-testid="reset-pw-confirm-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !token}
              className="w-full rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill h-11"
              data-testid="reset-submit-btn"
            >
              {loading ? "Working..." : "Set new password"}
            </Button>
            {!token && (
              <p className="text-xs text-red-600">
                Missing reset token — please open the link from your email.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
